import React, { useState, useEffect } from 'react';
import { MonetizationMetrics } from '../types/Analytics';
import { analyticsService } from '../services/analyticsService';
import { DollarSign, ShoppingCart, Eye, TrendingUp, Award, Loader2, PieChart as PieChartIcon, BarChart } from 'lucide-react';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
    BarChart as RechartsBarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid
} from 'recharts';

const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#ec4899'];

interface MonetizationChartProps {
    days: number;
    refreshKey?: number;
}

const MonetizationChart: React.FC<MonetizationChartProps> = ({ days, refreshKey }) => {
    const [metrics, setMetrics] = useState<MonetizationMetrics | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, refreshKey]);

    const loadMetrics = async () => {
        setLoading(true);
        try {
            const data = await analyticsService.getMonetizationMetrics(days);
            setMetrics(data);
        } catch (error) {
            console.error('Failed to load monetization metrics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Loader2 size={48} className="animate-spin" style={{ margin: '0 auto', color: '#3b82f6' }} />
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading monetization data...</p>
            </div>
        );
    }

    if (!metrics) {
        return <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>No monetization data available</div>;
    }

    return (
        <div>
            <h3 style={{ marginBottom: '1.5rem' }}>Monetization Overview</h3>

            {/* Summary Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    borderRadius: '8px',
                    color: 'white'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <DollarSign size={20} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Revenue</span>
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>${metrics.totalRevenue.toFixed(2)}</div>
                </div>

                <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                    borderRadius: '8px',
                    color: 'white'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <ShoppingCart size={20} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Purchases</span>
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{metrics.totalPurchases}</div>
                </div>

                <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                    borderRadius: '8px',
                    color: 'white'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <Eye size={20} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Ad Views</span>
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{metrics.totalAdViews}</div>
                </div>

                <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    borderRadius: '8px',
                    color: 'white'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <TrendingUp size={20} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>ARPU</span>
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>${metrics.avgRevenuePerUser.toFixed(2)}</div>
                </div>
            </div>

            {/* Charts Section */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '2rem',
                marginBottom: '2rem'
            }}>
                <div style={{
                    background: 'white',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    height: '400px'
                }}>
                    <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <PieChartIcon size={20} color="#8b5cf6" />
                        Revenue Distribution
                    </h4>
                    {metrics.topProducts.length === 0 ? (
                        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No content</p>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={metrics.topProducts}
                                    dataKey="revenue"
                                    nameKey="itemName"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                >
                                    {metrics.topProducts.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value: any) => [`$${parseFloat(value).toFixed(2)}`, 'Revenue']}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div style={{
                    background: 'white',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    height: '400px'
                }}>
                    <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <BarChart size={20} color="#10b981" />
                        Purchases by Product
                    </h4>
                    {metrics.topProducts.length === 0 ? (
                        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No content</p>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsBarChart
                                data={metrics.topProducts}
                                layout="vertical"
                                margin={{ left: 40, right: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                <XAxis type="number" hide />
                                <YAxis
                                    dataKey="itemName"
                                    type="category"
                                    tick={{ fontSize: 12 }}
                                    width={100}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    cursor={{ fill: 'transparent' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                />
                                <Bar dataKey="purchases" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                            </RechartsBarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Top Products List Detail */}
            <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
            }}>
                <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Award size={20} color="#f59e0b" />
                    Product Performance Details
                </h4>
                {metrics.topProducts.length === 0 ? (
                    <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No purchases yet</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {metrics.topProducts.map((product, index) => (
                            <div
                                key={product.productId}
                                style={{
                                    padding: '1rem',
                                    background: index === 0 ? '#fffbeb' : '#f9fafb',
                                    borderRadius: '4px',
                                    border: `1px solid ${index === 0 ? '#fef3c7' : '#e5e7eb'}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                        {product.itemName}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                        {product.purchases} purchases
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '1.25rem',
                                    fontWeight: 'bold',
                                    color: '#059669'
                                }}>
                                    ${product.revenue.toFixed(2)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonetizationChart;
